import { useEffect, useState } from 'react';
import { ArrowLeftRight, Loader2, CheckCircle2, AlertCircle } from 'lucide-react';
import { useAlert } from '../../../contexts/AlertContext';
import { useRendererSwitch } from '../../../hooks/useRendererSwitch';
import { BookFormat, EpubRendererType, BookProcessingJobType } from '../../../types/manageBooks';
import { SwitchRendererConfirmDialog } from './SwitchRendererConfirmDialog';
import { useTranslation } from '../../../i18n/useTranslation';

interface Props {
  bookId: string;
  bookTitle: string;
  /**
   * Format of the book — CustomHtml and EpubJs are both EPUB-only renderers,
   * so the switch action is hidden on PDF rows.
   */
  bookFormat: BookFormat;
  currentRenderer: EpubRendererType;
  /** True when the user holds the SwitchBookRenderer permission. */
  allowed: boolean;
  /**
   * When the page-level trigger matches this bookId, open the dialog.
   * Parent clears the trigger via `onDialogOpenedFor(bookId)` once the
   * dialog opens, so it isn't re-opened on rerenders.
   */
  externalTriggerBookId: string | null;
  onDialogOpenedFor: (bookId: string) => void;
}

function targetOf(current: EpubRendererType): EpubRendererType {
  return current === EpubRendererType.CustomHtml
    ? EpubRendererType.EpubJs
    : EpubRendererType.CustomHtml;
}

export function SwitchRendererAction({
  bookId,
  bookTitle,
  bookFormat,
  currentRenderer,
  allowed,
  externalTriggerBookId,
  onDialogOpenedFor,
}: Props) {
  const { t } = useTranslation();
  const [dialogOpen, setDialogOpen] = useState(false);
  const { showToast } = useAlert();
  const { phase, activeJobTypes, failedJobError, isSwitching, switchTo } =
    useRendererSwitch({ bookId });

  const jobTypeLabel = (jobType: BookProcessingJobType): string => {
    switch (jobType) {
      case BookProcessingJobType.SearchIndex: return t('App.Admin.Books.SwitchRendererAction.SearchIndexJob');
      case BookProcessingJobType.CfiLocations: return t('App.Admin.Books.SwitchRendererAction.CfiLocationsJob');
      case BookProcessingJobType.Upload: return t('App.Admin.Books.SwitchRendererAction.UploadJob');
      default: return `Job ${jobType}`;
    }
  };

  const targetLabel = (current: EpubRendererType): string => {
    const target = targetOf(current);
    return target === EpubRendererType.EpubJs
      ? t('App.Common.RendererType.EpubJs')
      : t('App.Common.RendererType.CustomHtml');
  };

  // Auto-open dialog when the page-level trigger matches this row.
  useEffect(() => {
    if (externalTriggerBookId === bookId && !dialogOpen) {
      setDialogOpen(true);
      onDialogOpenedFor(bookId);
    }
  }, [externalTriggerBookId, bookId, dialogOpen, onDialogOpenedFor]);

  // Renderer switching is EPUB-only (both CustomHtml and EpubJs render EPUB
  // content; PDFs always use UnifiedPdfRenderer). Render an em-dash on PDF
  // rows so the column still looks intentional, not broken. Placed after all
  // hooks so Rules of Hooks stays satisfied regardless of format changes.
  if (bookFormat !== BookFormat.EPUB) {
    return <span className="text-neutral-400" aria-label={t('App.Admin.Books.SwitchRendererAction.NotApplicableAriaLabel')}>&mdash;</span>;
  }

  const handleConfirm = async () => {
    const target = targetOf(currentRenderer);
    try {
      const newBook = await switchTo(target);
      setDialogOpen(false);
      showToast(
        t('App.Admin.Books.SwitchRendererAction.SwitchedSuccess', { target: targetLabel(currentRenderer), title: newBook.title }),
        { type: 'success' }
      );
    } catch (err) {
      setDialogOpen(false);
      const msg = err instanceof Error ? err.message : String(err);
      showToast(t('App.Admin.Books.SwitchRendererAction.SwitchFailed', { error: msg }), { type: 'error' });
    }
  };

  // Status pill rendering — mutually exclusive with the button.
  if (phase === 'switching') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200">
        <Loader2 className="w-3 h-3 animate-spin" aria-hidden="true" />
        {t('App.Admin.Books.SwitchRendererAction.SwitchingStatus')}
      </span>
    );
  }
  if (phase === 'indexing') {
    const tooltip = activeJobTypes.length
      ? t('App.Admin.Books.SwitchRendererAction.RunningJobsList', { jobs: activeJobTypes.map(jobTypeLabel).join(', ') })
      : t('App.Admin.Books.SwitchRendererAction.RunningJobs');
    return (
      <span
        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200"
        title={tooltip}
      >
        <Loader2 className="w-3 h-3 animate-spin" aria-hidden="true" />
        {t('App.Admin.Books.SwitchRendererAction.IndexingStatus')}
      </span>
    );
  }
  if (phase === 'ready') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium bg-green-50 text-green-700 border border-green-200">
        <CheckCircle2 className="w-3 h-3" aria-hidden="true" />
        {t('App.Admin.Books.SwitchRendererAction.ReadyStatus')}
      </span>
    );
  }

  // idle or failed → render button (Retry in failed state)
  const isRetry = phase === 'failed';
  const buttonLabel = isRetry
    ? t('App.Admin.Books.SwitchRendererAction.RetryButtonLabel', { target: targetLabel(currentRenderer) })
    : t('App.Admin.Books.SwitchRendererAction.SwitchButtonLabel', { target: targetLabel(currentRenderer) });
  const buttonTooltip = allowed
    ? (isRetry && failedJobError
        ? t('App.Admin.Books.SwitchRendererAction.FailedTooltip', { error: failedJobError, action: buttonLabel })
        : buttonLabel)
    : t('App.Admin.Books.SwitchRendererAction.NoPermissionTooltip');

  return (
    <>
      <button
        type="button"
        onClick={() => setDialogOpen(true)}
        disabled={!allowed || isSwitching}
        className={
          isRetry
            ? 'inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-red-700 bg-red-50 border border-red-200 rounded-md hover:bg-red-100 disabled:opacity-50 disabled:cursor-not-allowed min-h-[32px]'
            : 'inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-neutral-700 bg-white border border-neutral-300 rounded-md hover:bg-neutral-50 disabled:opacity-50 disabled:cursor-not-allowed min-h-[32px]'
        }
        aria-label={buttonLabel}
        title={buttonTooltip}
      >
        {isRetry ? (
          <AlertCircle className="w-3.5 h-3.5" aria-hidden="true" />
        ) : (
          <ArrowLeftRight className="w-3.5 h-3.5" aria-hidden="true" />
        )}
        <span className="hidden md:inline">
          {isRetry ? t('App.Admin.Books.SwitchRendererAction.RetryLabel') : t('App.Admin.Books.SwitchRendererAction.SwitchLabel')}
        </span>
      </button>

      <SwitchRendererConfirmDialog
        open={dialogOpen}
        bookTitle={bookTitle}
        currentRenderer={currentRenderer}
        isSubmitting={isSwitching}
        onConfirm={handleConfirm}
        onCancel={() => setDialogOpen(false)}
      />
    </>
  );
}
